import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {addCommas, addFromColumn, sortNumber} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import {RESOURCES as searchResources} from '../fuseSearchResourceMap';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import CircularProgress from 'material-ui/CircularProgress';
import FlatButton from 'material-ui/FlatButton';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import TableIcon from 'material-ui/svg-icons/action/card-membership';
import Config from '../messages';

/**
 * Class for retrieving and displaying VIP Signups by Location in a single table
 *
 */
class VIPSignupsReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            errorMessage: null,
            loading: false,
            reportName: 'VIP Signups',
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'Location ID', key: 'registration_location'},
                {label: 'Location', key: 'location_name'},
                {label: 'City', key: 'location_city'},
                {label: 'State', key: 'location_state'},
                {label: 'Signups', key: 'registrations'},
            ],
            summaryComplete: false,
        };
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        if (this.props.allowFetch) {
            this.fetchData();
        }
    }

    componentWillReceiveProps(nextProps) {
        // If fetch is activated, clear the table and set loading state
        if (nextProps.allowFetch && !this.props.allowFetch) {
            this.setState({
                loading: true,
                summaryComplete: false
            });
        }
    }

    componentDidUpdate(prevProps) {
        // If fetch and loading are activated, fetch data
        if (this.props.allowFetch && this.state.loading) {
            this.fetchData();
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get all Signups within provided date range
     *
     */
    fetchData() {
        const params = {
            start_date: this.props.startDate,
            end_date: this.props.endDate,
            offset: this.props.offset
        };
        const resource = searchResources.VIPSignups.GET_LIST(params);

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.length > 0) {
                this.processData(result.data);
            } else {
                this.processData([]);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                errorMessage: message,
                loading: false,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store results
     *
     * @param data
     */
    processData(data) {

        let summaryList = [];

        for (let i = 0; i < data.length; i++) {
            let result = data[i];
            const row = {
                dba_id: (result.dba && result.dba.id) ? result.dba.id : '(None)',
                dba_name: (result.dba && result.dba.name) ? result.dba.name : '(None)',
                location_city: (result.location_city) ? result.location_city : '(None)',
                location_name: (result.location_name) ? result.location_name : '(None)',
                location_state: (result.location_state) ? result.location_state : 'N/A',
                registration_location: (result.registration_location) ? result.registration_location : '(None)',
                registrations: (result.registrations) ? result.registrations : 0
            };
            summaryList.push(row);
        }

        // Finalize table data
        this.setState({
            errorMessage: null,
            loading: false,
            summaryList: summaryList,
            summaryTableData: summaryList,
            summaryComplete: true,
        }, () => {
            if (typeof this.props.onSummaryComplete === 'function') {
                this.props.onSummaryComplete(true);
            }
        });
    }

    /* Table Building ------------------------------ */

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                summaryTableData: tableData
            });
        }
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            system
        } = this.props;

        const {
            errorMessage,
            loading,
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
        } = this.state;

        const defaultSort = setDefaultTableSort({field: 'registration_location'});

        // ReactTable headers and columns for Table
        const tableColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Location ID',
                        accessor: 'registration_location',
                    },
                    {
                        Header: 'Location Name',
                        accessor: 'location_name',
                    },
                    {
                        Header: 'City',
                        accessor: 'location_city',
                    },
                    {
                        Header: 'State',
                        accessor: 'location_state',
                        width: 90
                    },
                    {
                        Header: 'Signups',
                        accessor: 'registrations',
                        sortMethod: (a, b) => sortNumber(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={tableStyles.footer}>
                                {addCommas(addFromColumn(summaryTableData, 'registrations'))}
                            </span>
                        )
                    }
                ]
            }
        ];

        return (
            <div>
                {loading &&
                    <div className="loader-container-left">
                        <h4>Generating Report...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            className="loader"
                        />
                    </div>
                }

                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: VIP Signups */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export VIP Signups */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={summaryTableData}
                                        headers={summaryTableHeaders}
                                        filename={'VIP-Signups-' + system + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export VIP Signups
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <TableIcon style={commonStyles.summaryIcon}/>
                                <span>VIP Signups</span>
                            </h3>
                        </div>

                        {/* Table: VIP Signups */}
                        <ReactTable
                            key="table-vip-signups"
                            data={summaryList}
                            columns={tableColumns}
                            defaultPageSize={10000}
                            showPagination={false}
                            defaultSorted={defaultSort}
                            filterable
                            defaultFilterMethod={filterCaseInsensitive}
                            onSortedChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                            onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.reactTable)}
                            ref={(r) => this.reactTable = r}
                            resizable={false}
                            minRows={1}
                            className="-striped no-print"
                        />
                        <ReactTable
                            key="table-vip-signups-print"
                            data={summaryTableData}
                            columns={tableColumns}
                            defaultPageSize={10000}
                            showPagination={false}
                            defaultSorted={defaultSort}
                            filterable
                            resizable={false}
                            minRows={1}
                            className="print-only"
                        />
                    </div>
                }

                {/* Error and Messaging */}
                {errorMessage &&
                    <div className="error">
                        <p>{errorMessage}</p>
                    </div>
                }
            </div>
        )
    }
}

VIPSignupsReport.propTypes = {
    addField: PropTypes.bool,
    allowFetch: PropTypes.bool,
    endDate: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onSummaryComplete: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
};

VIPSignupsReport.defaultProps = {
    addField: true,
    allowFetch: false,
    endDate: null,
    offset: null,
    startDate: null
};

export default VIPSignupsReport;



// WEBPACK FOOTER //
// ./src/Fuse/components/VIPSignupsReport.js